import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Copy,
  Download,
  Eye,
  History,
  Link2,
  PenLine,
  RotateCcw,
  Trash2,
  Upload,
} from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useCreateShare,
  useCreateSignatureRequest,
  useDeleteDocument,
  useDocument,
  useDownloadUrl,
  useRestoreVersion,
  useRevokeShare,
  useSaveDocument,
  useSendSignatureRequest,
  useUploadDocument,
} from './api';
import {
  CATEGORY_LABELS,
  CONFIDENTIALITY_HINTS,
  CONFIDENTIALITY_LABELS,
  CONFIDENTIALITY_VARIANT,
  SIGNATURE_STATUS_LABELS,
  SIGNATURE_STATUS_VARIANT,
} from './labels';
import type { VaultDocument } from './types';

export function DocumentDetailPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const { data: document, isLoading } = useDocument(documentId);
  const download = useDownloadUrl();
  const remove = useDeleteDocument();

  const [sharing, setSharing] = useState(false);
  const [signing, setSigning] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newVersion, setNewVersion] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isLoading) return <TableSkeleton columns={3} />;
  if (!document) return null;

  const open = async (versionId?: string) => {
    setError(null);
    try {
      const result = await download.mutateAsync({ id: document.id, versionId });
      window.open(result.url, '_blank', 'noopener');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not open that file.');
    }
  };

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: 'Documents', to: '/documents' },
          { label: document.name },
        ]}
        title={document.name}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{CATEGORY_LABELS[document.category]}</span>
            {document.folder && (
              <>
                <span>·</span>
                <Link
                  to={`/documents?folder=${document.folder.id}`}
                  className="text-primary hover:underline"
                >
                  {document.folder.path}
                </Link>
              </>
            )}
            {document.currentVersion && (
              <>
                <span>·</span>
                <span>
                  v{document.currentVersion.version} · {document.currentVersion.sizeLabel}
                </span>
              </>
            )}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={CONFIDENTIALITY_VARIANT[document.confidentiality]}>
              {CONFIDENTIALITY_LABELS[document.confidentiality]}
            </Badge>
            {document.abilities?.download && (
              <Button size="sm" variant="outline" onClick={() => void open()}>
                <Download className="h-4 w-4" />
                Download
              </Button>
            )}
            <Can permission="file.document.export">
              <Button size="sm" variant="outline" onClick={() => setSharing(true)}>
                <Link2 className="h-4 w-4" />
                Share
              </Button>
            </Can>
            <Can permission="file.document.create">
              <Button size="sm" variant="outline" onClick={() => setSigning(true)}>
                <PenLine className="h-4 w-4" />
                Request signature
              </Button>
            </Can>
            {document.abilities?.edit && (
              <>
                <Button size="sm" variant="outline" onClick={() => setNewVersion(true)}>
                  <Upload className="h-4 w-4" />
                  New version
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  Edit
                </Button>
              </>
            )}
          </div>
        }
      />

      <FormError message={error} />

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Versions document={document} onOpen={open} />
          <Signatures document={document} />
          <Shares document={document} />
        </div>

        <div className="space-y-5">
          <Details document={document} />
          <AccessLog document={document} />

          {document.abilities?.delete && (
            <Button
              variant="outline"
              className="w-full text-destructive"
              onClick={async () => {
                setError(null);
                try {
                  await remove.mutateAsync(document.id);
                  navigate('/documents');
                } catch (caught) {
                  setError(
                    caught instanceof Error ? caught.message : 'Could not delete the document.',
                  );
                }
              }}
            >
              <Trash2 className="h-4 w-4" />
              Delete document
            </Button>
          )}
        </div>
      </div>

      {sharing && <ShareDialog document={document} onClose={() => setSharing(false)} />}
      {signing && <SignatureDialog document={document} onClose={() => setSigning(false)} />}
      {editing && <EditDialog document={document} onClose={() => setEditing(false)} />}
      {newVersion && (
        <NewVersionDialog document={document} onClose={() => setNewVersion(false)} />
      )}
    </div>
  );
}

function Versions({
  document,
  onOpen,
}: {
  document: VaultDocument;
  onOpen: (versionId?: string) => void;
}) {
  const restore = useRestoreVersion(document.id);
  const versions = document.versions ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <History className="h-4 w-4 text-muted-foreground" />
          Versions
        </CardTitle>
        <CardDescription>
          Every upload keeps the one before it. Restoring moves the pointer — it never deletes
          what came after, so a restore can itself be undone.
        </CardDescription>
      </CardHeader>
      <CardContent className="divide-y p-0">
        {versions.map((version) => {
          const isCurrent = version.id === document.currentVersionId;
          return (
            <div key={version.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span
                className={cn(
                  'tabular flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-semibold',
                  isCurrent ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                )}
              >
                v{version.version}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{version.originalName}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {version.sizeLabel} · {formatDateTime(version.createdAt)}
                  {version.uploadedBy && ` · ${version.uploadedBy.name}`}
                  {version.changeNote && ` — ${version.changeNote}`}
                </div>
              </div>
              {isCurrent && <Badge className="shrink-0 text-[10px]">current</Badge>}
              <button
                className="shrink-0 text-xs text-primary hover:underline"
                onClick={() => onOpen(version.id)}
              >
                Open
              </button>
              {!isCurrent && document.abilities?.edit && (
                <button
                  className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => restore.mutate(version.id)}
                >
                  <RotateCcw className="h-3 w-3" />
                  Restore
                </button>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function Signatures({ document }: { document: VaultDocument }) {
  const send = useSendSignatureRequest();
  const requests = document.signatureRequests ?? [];
  if (requests.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <PenLine className="h-4 w-4 text-muted-foreground" />
          Signatures
        </CardTitle>
        <CardDescription>
          The certificate carries the SHA-256 of the exact file that was signed, so anyone can
          check the document they are holding is the one people agreed to.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {requests.map((request) => (
          <div key={request.id} className="rounded-lg border p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Link
                to={`/documents/signatures/${request.id}`}
                className="font-medium hover:underline"
              >
                {request.title}
              </Link>
              <div className="flex items-center gap-2">
                <Badge variant={SIGNATURE_STATUS_VARIANT[request.status]}>
                  {SIGNATURE_STATUS_LABELS[request.status]}
                </Badge>
                {request.status === 'DRAFT' && (
                  <Button size="sm" onClick={() => send.mutate(request.id)}>
                    Send
                  </Button>
                )}
              </div>
            </div>
            <ul className="mt-2 space-y-1">
              {request.signers.map((signer) => (
                <li key={signer.id} className="flex items-center justify-between text-sm">
                  <span>
                    {signer.name}
                    <span className="ml-2 text-xs text-muted-foreground">{signer.email}</span>
                  </span>
                  <span
                    className={cn(
                      'text-xs',
                      signer.signedAt ? 'text-emerald-600' : 'text-muted-foreground',
                    )}
                  >
                    {signer.signedAt ? `signed ${formatDate(signer.signedAt)}` : signer.status.toLowerCase()}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function Shares({ document }: { document: VaultDocument }) {
  const revoke = useRevokeShare(document.id);
  const shares = document.shares ?? [];
  if (shares.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          Shared links
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {shares.map((share) => {
          const dead =
            Boolean(share.revokedAt) ||
            (share.expiresAt ? new Date(share.expiresAt) < new Date() : false);

          return (
            <div
              key={share.id}
              className={cn('flex flex-wrap items-center gap-3 rounded-lg border p-3', dead && 'opacity-60')}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm">
                  <code className="truncate text-xs">/d/{share.token.slice(0, 12)}…</code>
                  <button
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      void navigator.clipboard.writeText(
                        `${window.location.origin}/d/${share.token}`,
                      )
                    }
                    aria-label="Copy link"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="text-xs text-muted-foreground">
                  {share.viewCount} views · {share.downloadCount} downloads
                  {share.downloadLimit !== null && ` of ${share.downloadLimit}`}
                  {share.expiresAt && ` · expires ${formatDate(share.expiresAt)}`}
                  {share.viewOnly && ' · view only'}
                  {share.revokedAt && ' · revoked'}
                </div>
              </div>
              {!dead && (
                <Button size="sm" variant="ghost" onClick={() => revoke.mutate(share.id)}>
                  Revoke
                </Button>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function Details({ document }: { document: VaultDocument }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <Row label="Owner" value={document.owner?.name ?? 'Nobody'} />
        <Row label="Added" value={formatDate(document.createdAt)} />
        <Row label="Last change" value={formatDate(document.updatedAt)} />
        {document.expiryDate && (
          <Row
            label="Expires"
            value={formatDate(document.expiryDate)}
            tone={new Date(document.expiryDate) < new Date() ? 'bad' : undefined}
          />
        )}
        {document.currentVersion?.checksum && (
          <div className="border-t pt-2">
            <div className="text-xs text-muted-foreground">SHA-256</div>
            <code className="block break-all text-[11px]">{document.currentVersion.checksum}</code>
          </div>
        )}
        {document.description && (
          <div className="border-t pt-2">
            <div className="mb-1 text-xs text-muted-foreground">Description</div>
            <p className="whitespace-pre-line">{document.description}</p>
          </div>
        )}
        {document.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 border-t pt-2">
            {document.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-[10px]">
                {tag}
              </Badge>
            ))}
          </div>
        )}
        {document.currentVersion?.scanStatus === 'NOT_SCANNED' && (
          <p className="border-t pt-2 text-xs text-muted-foreground">
            This file has not been scanned for malware — no scanner is wired up on this server.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function AccessLog({ document }: { document: VaultDocument }) {
  const logs = document.accessLogs ?? [];
  if (logs.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Eye className="h-4 w-4 text-muted-foreground" />
          Who has looked
        </CardTitle>
        <CardDescription>Every attempt, allowed or refused.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {logs.slice(0, 20).map((log) => (
          <div key={log.id} className="flex items-center justify-between text-xs">
            <span className={cn(!log.allowed && 'text-destructive')}>
              {log.user?.name ?? 'A link visitor'} — {log.action}
              {!log.allowed && ' (refused)'}
            </span>
            <span className="text-muted-foreground">{formatDateTime(log.createdAt)}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ShareDialog({ document, onClose }: { document: VaultDocument; onClose: () => void }) {
  const create = useCreateShare(document.id);
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [downloadLimit, setDownloadLimit] = useState<number | ''>('');
  const [viewOnly, setViewOnly] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const restricted = document.confidentiality === 'RESTRICTED';

  if (url) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link ready</DialogTitle>
            <DialogDescription>
              Anyone with this link can open the document under the limits you set. Revoke it at
              any time from the document.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-3">
            <code className="min-w-0 flex-1 break-all text-xs">{url}</code>
            <Button size="sm" variant="outline" onClick={() => void navigator.clipboard.writeText(url)}>
              <Copy className="h-3.5 w-3.5" />
              Copy
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={onClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share {document.name}</DialogTitle>
          <DialogDescription>
            A link that works outside the workspace. Everything limiting it is checked on the
            server, so revoking is immediate rather than a matter of hoping nobody kept it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Password {restricted && <span className="text-destructive">(required)</span>}</Label>
            <Input
              type="password"
              value={password}
              placeholder={restricted ? 'A restricted document needs one' : 'Optional'}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Expires</Label>
              <Input
                type="date"
                value={expiresAt}
                onChange={(event) => setExpiresAt(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Download limit</Label>
              <Input
                type="number"
                min={1}
                placeholder="Unlimited"
                value={downloadLimit}
                onChange={(event) =>
                  setDownloadLimit(event.target.value ? Number(event.target.value) : '')
                }
              />
            </div>
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 rounded-md border p-3">
            <Checkbox checked={viewOnly} onCheckedChange={(value) => setViewOnly(value === true)} />
            <span className="text-sm">
              View only
              <span className="block text-xs text-muted-foreground">
                Hides the download button and refuses the download endpoint.
              </span>
            </span>
          </label>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={create.isPending || (restricted && password.length < 4)}
            onClick={async () => {
              setError(null);
              try {
                const share = await create.mutateAsync({
                  password: password || undefined,
                  expiresAt: expiresAt || undefined,
                  downloadLimit: downloadLimit || undefined,
                  viewOnly,
                });
                setUrl(share.url);
              } catch (caught) {
                setError(
                  caught instanceof Error ? caught.message : 'Could not create the link.',
                );
              }
            }}
          >
            Create link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignatureDialog({ document, onClose }: { document: VaultDocument; onClose: () => void }) {
  const create = useCreateSignatureRequest();
  const send = useSendSignatureRequest();
  const [title, setTitle] = useState(document.name);
  const [message, setMessage] = useState('');
  const [sequential, setSequential] = useState(false);
  const [signers, setSigners] = useState([{ name: '', email: '' }]);
  const [error, setError] = useState<string | null>(null);

  const valid = signers.every((signer) => signer.name.trim() && signer.email.includes('@'));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request signatures</DialogTitle>
          <DialogDescription>
            Each signer gets a private link. The bytes are hashed when the request is sent, so
            the file can be changed until that moment and not after.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] space-y-4 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label>What they are signing</Label>
            <Input value={title} onChange={(event) => setTitle(event.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea
              rows={2}
              value={message}
              placeholder="Included in the email they receive"
              onChange={(event) => setMessage(event.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Signers</Label>
            {signers.map((signer, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  className="h-9"
                  placeholder="Name"
                  value={signer.name}
                  onChange={(event) => {
                    const next = [...signers];
                    next[index] = { ...signer, name: event.target.value };
                    setSigners(next);
                  }}
                />
                <Input
                  className="h-9"
                  placeholder="Email"
                  type="email"
                  value={signer.email}
                  onChange={(event) => {
                    const next = [...signers];
                    next[index] = { ...signer, email: event.target.value };
                    setSigners(next);
                  }}
                />
                {signers.length > 1 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSigners(signers.filter((_, i) => i !== index))}
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSigners([...signers, { name: '', email: '' }])}
            >
              Add another signer
            </Button>
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 rounded-md border p-3">
            <Checkbox
              checked={sequential}
              onCheckedChange={(value) => setSequential(value === true)}
            />
            <span className="text-sm">
              One after another
              <span className="block text-xs text-muted-foreground">
                Each person is invited only once the one before has signed.
              </span>
            </span>
          </label>

          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={create.isPending || !valid || title.trim().length < 2}
            onClick={async () => {
              setError(null);
              try {
                const request = await create.mutateAsync({
                  documentId: document.id,
                  title,
                  message: message || undefined,
                  sequential,
                  signers,
                });
                await send.mutateAsync(request.id);
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof Error ? caught.message : 'Could not send the request.',
                );
              }
            }}
          >
            {create.isPending || send.isPending ? 'Sending…' : 'Send for signature'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ document, onClose }: { document: VaultDocument; onClose: () => void }) {
  const save = useSaveDocument();
  const { data: users = [] } = useUserOptions();
  const [form, setForm] = useState({
    name: document.name,
    description: document.description ?? '',
    category: document.category,
    confidentiality: document.confidentiality,
    tags: document.tags.join(', '),
    ownerId: document.ownerId ?? '',
    expiryDate: document.expiryDate?.slice(0, 10) ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {document.name}</DialogTitle>
        </DialogHeader>

        <div className="max-h-[55vh] space-y-4 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value as never })}
              >
                {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Who can see it</Label>
              <Select
                value={form.confidentiality}
                onChange={(event) =>
                  setForm({ ...form, confidentiality: event.target.value as never })
                }
              >
                {Object.entries(CONFIDENTIALITY_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">
                {CONFIDENTIALITY_HINTS[form.confidentiality]}
              </p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Select
                value={form.ownerId}
                onChange={(event) => setForm({ ...form, ownerId: event.target.value })}
              >
                <option value="">Nobody</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Expires</Label>
              <Input
                type="date"
                value={form.expiryDate}
                onChange={(event) => setForm({ ...form, expiryDate: event.target.value })}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Tags</Label>
            <Input
              value={form.tags}
              onChange={(event) => setForm({ ...form, tags: event.target.value })}
            />
          </div>
          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={save.isPending}
            onClick={async () => {
              setError(null);
              try {
                await save.mutateAsync({
                  id: document.id,
                  name: form.name,
                  description: form.description || undefined,
                  category: form.category,
                  confidentiality: form.confidentiality,
                  tags: form.tags
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean),
                  ownerId: form.ownerId || undefined,
                  expiryDate: form.expiryDate || undefined,
                });
                onClose();
              } catch (caught) {
                setError(caught instanceof Error ? caught.message : 'Could not save it.');
              }
            }}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewVersionDialog({ document, onClose }: { document: VaultDocument; onClose: () => void }) {
  const upload = useUploadDocument();
  const [file, setFile] = useState<File | null>(null);
  const [changeNote, setChangeNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload a new version</DialogTitle>
          <DialogDescription>
            The current version stays exactly where it is. Nothing is replaced.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            type="file"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
          <div className="space-y-1.5">
            <Label>What changed</Label>
            <Textarea
              rows={2}
              value={changeNote}
              onChange={(event) => setChangeNote(event.target.value)}
            />
          </div>
          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!file || upload.isPending}
            onClick={async () => {
              if (!file) return;
              setError(null);
              try {
                await upload.mutateAsync({
                  file,
                  meta: { documentId: document.id, changeNote: changeNote || undefined },
                });
                onClose();
              } catch (caught) {
                setError(caught instanceof Error ? caught.message : 'Could not upload it.');
              }
            }}
          >
            {upload.isPending ? 'Uploading…' : 'Upload version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone?: 'bad';
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('font-medium', tone === 'bad' && 'text-destructive')}>{value}</span>
    </div>
  );
}
